import { FC, useEffect, useState } from 'react';
import { Box, Button, Divider, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import {
  EditableCell,
  ImageModal,
  Loader,
  ManyFilesUpload,
  Page,
  PageTitleWithButton,
  Table
} from '../../../../../components';
import { Add, Delete, Edit } from '@mui/icons-material';

import { IColorPaletteDetail, IColorPaletteOption } from '../../../../../models';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import {
  createColorPaletteOptions,
  deleteColorPaletteOptions,
  getColorPaletteDetails,
  updateColorPaletteOptionFile,
  updateColorPaletteOptions
} from '../../../../../fetch/color-palettes';
import { useSnackbar } from 'notistack';

interface ISetsAndPalettesDetails {}

export const SetsAndPalettesDetails: FC<ISetsAndPalettesDetails> = () => {
  const classes = useStyles();
  const [currentImage, setCurrentImage] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();

  const { colorPaletteId }: { colorPaletteId: string } = useParams();
  const [isSavingOption, setIsSavingOption] = useState<boolean>(false);

  const [editableColorPaletteOptionRows, setEditableColorPaletteOptionRows] = useState<
    IColorPaletteOption[]
  >([]);
  const {
    isLoading: isLoadingColorPaletteOptions,
    data: colorPaletteOptions,
    refetch: refetchColorPaletteOptions,
    isRefetching: isRefetchingColorPaletteOptions
  } = useQuery<IColorPaletteDetail, Error>(
    ['colorPaletteDetail'],
    () => getColorPaletteDetails(colorPaletteId),
    {
      onSuccess: detail =>
        setEditableColorPaletteOptionRows(
          detail.options.length > 0
            ? detail.options
            : [{ name: '', imageUrl: '', colorPaletteOptionId: `new-${new Date().getTime()}` }]
        ),
      enabled: typeof colorPaletteId === 'string'
    }
  );

  const [editingId, setEditingId] = useState<string | number | null>(null);

  const handleEdit = (id: number | string | null) => {
    setEditingId(id);
  };

  const handleSave = async (item: IColorPaletteOption) => {
    if (item?.colorPaletteOptionId.toString().includes('new-')) {
      await handleCreate(item);
    } else {
      await handleUpdate(item);
    }
  };

  const handleCreate = async (item: IColorPaletteOption) => {
    setIsSavingOption(true);
    try {
      await createColorPaletteOptions(colorPaletteId, { name: item.name });
      enqueueSnackbar('Option Created!', { variant: 'success' });
      refetchColorPaletteOptions();
      setEditingId(null);
    } catch (error) {
      enqueueSnackbar(error?.Detail, { variant: 'error' });
    } finally {
      setIsSavingOption(false);
    }
  };
  const handleUpdate = async (item: IColorPaletteOption) => {
    setIsSavingOption(true);
    try {
      await updateColorPaletteOptions(item.colorPaletteOptionId.toString(), { name: item.name });
      enqueueSnackbar('Option Updated!', { variant: 'success' });
      refetchColorPaletteOptions();
      setEditingId(null);
    } catch (error) {
      enqueueSnackbar(error?.Detail, { variant: 'error' });
    } finally {
      setIsSavingOption(false);
    }
  };
  const handleDelete = async (item: IColorPaletteOption) => {
    setIsSavingOption(true);
    try {
      if (item.colorPaletteOptionId.toString().includes('new-')) {
        setEditableColorPaletteOptionRows(prev =>
          prev.filter(row => row.colorPaletteOptionId !== item.colorPaletteOptionId)
        );
      } else {
        await deleteColorPaletteOptions(item.colorPaletteOptionId.toString());
        refetchColorPaletteOptions();
      }
      enqueueSnackbar('Option Deleted!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error?.Detail, { variant: 'error' });
    } finally {
      setEditingId(null);
      setIsSavingOption(false);
    }
  };

  const handleAddOption = () => {
    const newOptionId = `new-${new Date().getTime()}`;
    setEditableColorPaletteOptionRows(prev => [
      ...prev,
      { name: '', imageUrl: '', colorPaletteOptionId: newOptionId }
    ]);
    setEditingId(newOptionId);
  };

  const handleImagesUpload = async (files: File[], item: IColorPaletteOption): Promise<any> => {
    let id = item.colorPaletteOptionId;
    if (item?.colorPaletteOptionId?.toString()?.includes('new-')) {
      try {
        const res = await createColorPaletteOptions(colorPaletteId, { name: item.name });
        id = res;
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? 'Something went wrong.', { variant: 'error' });
        setIsSavingOption(false);
      }
    }
    if (files?.length > 0) {
      try {
        setIsSavingOption(true);
        const formData = new FormData();
        formData.append('ImageFile', files[0]);

        const res = await updateColorPaletteOptionFile(id, formData);
        enqueueSnackbar('Option Updated!', { variant: 'success' });

        refetchColorPaletteOptions();
        handleEdit(null);
        setIsSavingOption(false);
        return res;
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? 'Something went wrong.', { variant: 'error' });
        setIsSavingOption(false);
      }
    }
  };

  const updateData = (
    colorPaletteOption: IColorPaletteOption,
    columnId: string,
    value: string,
    error?: boolean
  ) => {
    setEditableColorPaletteOptionRows(old => {
      return old.map(row => {
        if (row.colorPaletteOptionId === colorPaletteOption.colorPaletteOptionId) {
          return {
            ...colorPaletteOption,
            [columnId]: value
          };
        }
        return row;
      });
    });
  };

  useEffect(() => {
    if (
      editableColorPaletteOptionRows.length === 1 &&
      editableColorPaletteOptionRows?.[0]?.colorPaletteOptionId?.toString()?.includes('new-')
    ) {
      setEditingId(editableColorPaletteOptionRows?.[0]?.colorPaletteOptionId);
    }
  }, [editableColorPaletteOptionRows]);

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: props => {
        return editingId === props?.cell?.row?.original?.colorPaletteOptionId ? (
          <EditableCell {...props} />
        ) : (
          <span>{props?.cell?.row?.original?.name}</span>
        );
      }
    },
    {
      Header: 'Image',
      accessor: 'files',
      Cell: ({ cell: { row } }) => {
        const item: IColorPaletteOption = row.original;
        const isEditing = editingId === item.colorPaletteOptionId;
        return (
          <Box display="flex" alignItems="center">
            <>
              <Divider flexItem orientation="vertical" sx={{ margin: '0 0.5rem' }} />
            </>
            {item.imageUrl && (
              <picture
                className={classes.pictureImages}
                onClick={() => {
                  setCurrentImage(item.imageUrl);
                }}
              >
                <img className={classes.colorImage} src={item.imageUrl} alt={`${item.name}`} />
              </picture>
            )}
            {isEditing && (
              <ManyFilesUpload
                single
                isDisabled={isSavingOption[item.colorPaletteOptionId] || item.name === ''}
                imageKey={`upload-images-${item.colorPaletteOptionId}`}
                uploadText={item.imageUrl ? 'Change' : 'Upload'}
                accept={'image/*'}
                handleFileChange={async (files: File[]) => {
                  const res = await handleImagesUpload(files, item);
                  updateData(row.original, 'imageUrl', res);
                }}
              />
            )}
          </Box>
        );
      }
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      sort: false,
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IColorPaletteOption } };
      }) => {
        return (
          <div className={classes.actionButtons}>
            <Button
              id="edit"
              color="primary"
              startIcon={<Edit />}
              className={classes.editButton}
              disabled={!!editingId && original.colorPaletteOptionId !== editingId}
              onClick={() => {
                if (original.colorPaletteOptionId === editingId) {
                  handleSave(original);
                } else {
                  handleEdit(original.colorPaletteOptionId);
                }
              }}
            >
              {original.colorPaletteOptionId === editingId ? 'Save' : 'Edit'}
            </Button>
            <Button
              id="delete"
              color="primary"
              className={classes.deleteButton}
              startIcon={<Delete />}
              disabled={!!editingId && original.colorPaletteOptionId !== editingId}
              onClick={() => {
                const result = window.confirm(
                  `Are you sure you want to delete this Color Palette Option?`
                );
                if (result) {
                  handleDelete(original);
                } else {
                  return;
                }
              }}
            >
              Delete
            </Button>
          </div>
        );
      }
    }
  ];

  return (
    <Page title={colorPaletteOptions?.name || 'Option Sets and Palettes Details'}>
      <PageTitleWithButton
        pageTitleProps={{
          title: colorPaletteOptions?.name || 'Option Sets and Palettes Details'
        }}
        button={
          <Box display="flex">
            <Button
              id="add-option"
              startIcon={<Add />}
              color="primary"
              variant="contained"
              className={classes.addOptionButton}
              onClick={handleAddOption}
            >
              Add
            </Button>
          </Box>
        }
      />
      <>
        {(isLoadingColorPaletteOptions || isRefetchingColorPaletteOptions || isSavingOption) && (
          <Loader type="overlay" position="centered" />
        )}
        <Box mt={1}>
          <Table
            columns={columns}
            data={editableColorPaletteOptionRows}
            isLoading={isLoadingColorPaletteOptions}
            hidePagination
            useTableProps={{
              updateMyData: updateData
            }}
          />
        </Box>
        <ImageModal
          isOpen={!!currentImage}
          onClose={() => setCurrentImage('')}
          source={currentImage}
        />
      </>
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: '1rem'
  },
  link: {
    textDecoration: 'none'
  },
  addOptionButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  },
  deleteOptionButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    color: theme.palette.error.main,
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  },
  actionButtons: {
    textAlign: 'right',
    display: 'flex'
  },
  tabBar: {
    marginTop: '-1rem',
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  },
  buttonText: {
    color: 'white'
  },
  editButton: {
    textAlign: 'right'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  },
  pictureImages: {
    marginRight: theme.spacing(1),
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover::before': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      color: theme.palette.common.white,
      content: '"See Larger"',
      fontSize: '0.85rem',
      position: 'absolute',
      textAlign: 'center',
      height: 50,
      width: 50,
      padding: 4
    }
  },
  colorImage: {
    width: 50,
    height: 50
  }
}));
